import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useBudgetAssistantStore } from '@flows.cash/core'
import { useAuthStore } from '@/store/authStore'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { PiggyBank, Target, Calendar, Info } from 'lucide-react'

interface SavingsGoalsStepProps {
  onNext: () => void
  onSkip: () => void
}

export function SavingsGoalsStep({ onNext, onSkip }: SavingsGoalsStepProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { responses, updateResponses } = useBudgetAssistantStore()
  
  const [monthlySavings, setMonthlySavings] = useState('')
  const [upcomingExpenses, setUpcomingExpenses] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Load existing responses into local state
  useEffect(() => {
    if (responses.desiredSavings) {
      setMonthlySavings(responses.desiredSavings.toString())
    }
    if (responses.upcomingExpenses) {
      setUpcomingExpenses(responses.upcomingExpenses)
    }
  }, [responses])

  const handleNext = async () => {
    if (user?.id) {
      setIsLoading(true)
      try {
        const savingsData = {
          desiredSavings: monthlySavings ? parseFloat(monthlySavings) : undefined,
          upcomingExpenses: upcomingExpenses.trim() || undefined
        }
        
        await updateResponses(savingsData, 'savings_goals')
        onNext()
      } catch (error) {
        console.error('Error saving savings goals data:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const getSavingsInsight = () => {
    const amount = parseFloat(monthlySavings) || 0
    if (amount === 0) return null
    
    const yearly = amount * 12
    return yearly
  }

  const yearlyTotal = getSavingsInsight()

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="relative mx-auto w-14 h-14 mb-3">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-3 shadow-lg">
            <PiggyBank className="h-8 w-8 text-white" />
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('budgetAssistant.savingsGoals.title', 'Let\'s plan for your future')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
          {t('budgetAssistant.savingsGoals.subtitle', 'Saving money is one of the best habits you can build. Let\'s set some realistic goals!')}
        </p>
      </div>

      <div className="space-y-5">
        {/* Monthly Savings */}
        <Card className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/30">
              <Target className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <Label htmlFor="savings" className="text-sm font-medium">
                  {t('budgetAssistant.savingsGoals.monthly.label', 'Monthly Savings Goal')}
                </Label>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t('budgetAssistant.savingsGoals.monthly.description', 'How much would you like to save each month?')}
                </p>
              </div>
              <div className="relative max-w-xs">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  $
                </div>
                <Input
                  id="savings"
                  type="number"
                  placeholder="0.00"
                  value={monthlySavings}
                  onChange={(e) => setMonthlySavings(e.target.value)}
                  className="pl-8 h-10 text-sm"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Upcoming Expenses */}
        <Card className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <Label htmlFor="upcoming" className="text-sm font-medium">
                  {t('budgetAssistant.savingsGoals.upcoming.label', 'Upcoming Expenses')}
                </Label>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t('budgetAssistant.savingsGoals.upcoming.description', 'Any big expenses coming up? (vacations, car repairs, etc.)')}
                </p>
              </div>
              <Textarea
                id="upcoming"
                placeholder={t('budgetAssistant.savingsGoals.upcoming.placeholder', 'e.g., Vacation in 6 months ($2000), Car maintenance, Holiday gifts...')}
                value={upcomingExpenses}
                onChange={(e) => setUpcomingExpenses(e.target.value)}
                className="min-h-[80px] text-sm"
                rows={3}
              />
            </div>
          </div>
        </Card>

        {/* Savings Insights */}
        {monthlySavings && parseFloat(monthlySavings) > 0 && (
          <Card className="p-5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
            <div className="space-y-3">
              <h4 className="font-medium text-green-900 dark:text-green-100 text-sm flex items-center gap-2">
                <Target className="h-4 w-4" />
                {t('budgetAssistant.savingsGoals.insights.title', 'Your Savings Plan')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="text-center">
                  <div className="font-semibold text-green-800 dark:text-green-200 text-sm">
                    ${(parseFloat(monthlySavings) * 12).toLocaleString()}
                  </div>
                  <div className="text-green-600 dark:text-green-400 text-xs">
                    {t('budgetAssistant.savingsGoals.insights.yearly', 'Saved per year')}
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-green-800 dark:text-green-200 text-sm">
                    ${(parseFloat(monthlySavings) * 60).toLocaleString()}
                  </div>
                  <div className="text-green-600 dark:text-green-400 text-xs">
                    {t('budgetAssistant.savingsGoals.insights.fiveYear', 'In 5 years')}
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-green-800 dark:text-green-200 text-sm">
                    ${(parseFloat(monthlySavings) * 120).toLocaleString()}
                  </div>
                  <div className="text-green-600 dark:text-green-400 text-xs">
                    {t('budgetAssistant.savingsGoals.insights.tenYear', 'In 10 years')}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Savings Tips */}
        <Card className="p-4 bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1 text-sm">
                {t('budgetAssistant.savingsGoals.tips.title', 'Savings Tips')}
              </h4>
              <div className="space-y-1">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  • {t('budgetAssistant.savingsGoals.tips.tip1', 'Start small - even $25/month adds up over time')}
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  • {t('budgetAssistant.savingsGoals.tips.tip2', 'Automate your savings to make it easier')}
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  • {t('budgetAssistant.savingsGoals.tips.tip3', 'Consider building an emergency fund first (3-6 months of expenses)')}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button
          variant="outline"
          onClick={onSkip}
          className="text-gray-600 border-gray-300 hover:bg-gray-50 h-10"
        >
          {t('common.skip', 'Skip')}
        </Button>
        
        <Button
          onClick={handleNext}
          disabled={isLoading}
          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 h-10"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            t('common.continue', 'Continue')
          )}
        </Button>
      </div>
    </div>
  )
} 